import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Coins, Search, Flame } from 'lucide-react';
import { PageHeader } from '../components/Layout';
import { Input } from '../components/ui/input';
import { SEOHead } from '../components/SEOHead';
import { getLocalizedText } from '../components/MultiLanguageInput';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Pools = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Current language for multi-language content
  const currentLang = i18n.language || 'en';

  useEffect(() => {
    fetchPools();
  }, []);

  const fetchPools = async () => {
    try {
      const res = await axios.get(`${API}/pools`);
      setPools(res.data);
    } catch (err) {
      console.error('Failed to fetch pools:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredPools = pools.filter(p => {
    const poolName = getLocalizedText(p.name, currentLang);
    return poolName.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <SEOHead page="pools" />
      <PageHeader title={t('pools.title')} />
      
      {/* Search */}
      <div className="px-5 py-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-600" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('common.search') + '...'}
            className="h-12 pl-12 bg-white border-[#F0E3C8] text-[#1A1206] placeholder:text-[#9A8F79] rounded-xl"
            data-testid="pools-search"
          />
        </div>
      </div>

      {/* Pool Grid */}
      <div className="px-5 pb-24">
        {loading ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 2xl:grid-cols-7 gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <div key={i} className="aspect-[3/4] bg-white rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filteredPools.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-zinc-500">{t('pools.noPoolsFound')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 2xl:grid-cols-7 gap-3">
            {filteredPools.map((pool, index) => {
              const stockPercent = pool.total_quantity > 0 ? (pool.remaining_quantity / pool.total_quantity) * 100 : 0;
              const isLowStock = stockPercent <= 20 && stockPercent > 0;
              const isSoldOut = pool.remaining_quantity === 0;
              
              // Different colors for each category
              const categoryColors = {
                'Pokemon TCG': 'bg-yellow-600/90',
                'Gadget': 'bg-blue-600/90',
                'Ichiban Kuji': 'bg-pink-600/90',
                'E-Voucher': 'bg-emerald-600/90',
                'default': 'bg-purple-600/90'
              };
              // Get localized category name
              const localizedCategoryName = getLocalizedText(pool.category_name, currentLang);
              const categoryColor = localizedCategoryName ? (categoryColors[localizedCategoryName] || categoryColors['default']) : categoryColors['default'];
              
              return (
                <motion.div
                  key={pool.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.03, y: -5 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => navigate(`/pool/${pool.id}`)}
                  className="relative aspect-[3/4] rounded-xl overflow-hidden cursor-pointer group bg-[#FFF8EA]"
                  data-testid={`pool-item-${pool.id}`}
                >
                  <img 
                    src={pool.image_url} 
                    alt={getLocalizedText(pool.name, currentLang)}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0e2a45] via-[#0e2a45]/50 to-transparent" />
                  <div className="absolute inset-0 border border-white/10 rounded-xl group-hover:border-amber-500/50 transition-colors" />
                  
                  {/* Category Badge - Top Right */}
                  {localizedCategoryName && (
                    <div className="absolute top-2 right-2 z-10">
                      <span className={`px-2 py-1 text-[10px] font-bold ${categoryColor} text-white rounded-lg shadow-lg backdrop-blur-sm`}>
                        {localizedCategoryName.length > 12 ? localizedCategoryName.substring(0, 12) + '...' : localizedCategoryName}
                      </span>
                    </div>
                  )}
                  
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <h4 className="text-white font-bold text-sm font-['Chakra_Petch'] mb-1 truncate">{getLocalizedText(pool.name, currentLang)}</h4>
                    
                    {/* Stock Progress Bar */}
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Flame className={`w-3 h-3 ${isSoldOut ? 'text-zinc-600' : isLowStock ? 'text-orange-500 animate-pulse' : 'text-orange-500'}`} />
                      <div className="flex-1 h-1 bg-[#F0E3C8] rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${isSoldOut ? 'bg-zinc-700' : isLowStock ? 'bg-gradient-to-r from-orange-500 to-amber-500' : 'bg-gradient-to-r from-green-500 to-emerald-500'}`}
                          style={{ width: `${stockPercent}%` }}
                        />
                      </div>
                      <span className={`text-[9px] ${isSoldOut ? 'text-zinc-500' : isLowStock ? 'text-orange-400' : 'text-zinc-400'}`}>
                        {pool.remaining_quantity}/{pool.total_quantity}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <Coins className="w-3.5 h-3.5 text-yellow-500" />
                      <span className="text-white text-xs font-semibold">{pool.price} {t('wallet.coins')}</span>
                    </div>
                  </div>

                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-[radial-gradient(circle_at_50%_50%,rgba(220,38,38,0.2),transparent_70%)]" />
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
